import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { format, startOfWeek, addDays, isSameDay, startOfMonth, endOfMonth, addMonths, subMonths } from "date-fns";
import { EventCard } from "./EventCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Cloud, CloudRain, Sun, CloudDrizzle } from "lucide-react";

interface CalendarViewProps {
  view: 'month' | 'week' | 'day' | 'agenda';
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onEventClick: (event: any) => void;
  filters: any;
}

interface WeatherData {
  date: string;
  temp_f: number;
  condition: string;
  icon: string;
  precip_chance: number;
}

export function CalendarView({ view, selectedDate, onDateSelect, onEventClick, filters }: CalendarViewProps) {
  const { orgId } = useAuth();
  const [events, setEvents] = useState<any[]>([]);
  const [weather, setWeather] = useState<WeatherData[]>([]);
  const [weatherLocation, setWeatherLocation] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [weatherLoading, setWeatherLoading] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    fetchEvents();
  }, [orgId, selectedDate, filters.eventType]);

  // Fetch weather when showWeather is enabled, regardless of events
  useEffect(() => {
    if (filters.showWeather && orgId) {
      fetchWeather();
    }
  }, [filters.showWeather, view, orgId, selectedDate]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const start = view === 'month' ? startOfMonth(selectedDate) : startOfWeek(selectedDate);
      const end = view === 'month' ? endOfMonth(selectedDate) : addDays(startOfWeek(selectedDate), 7);

      const { data, error } = await supabase.functions.invoke('calendar-events-crud', {
        body: {
          action: 'list',
          start_date: start.toISOString(),
          end_date: end.toISOString(),
          event_type: filters.eventType !== 'all' ? filters.eventType : undefined
        }
      });

      if (error) throw error;
      setEvents(data.events || []);
    } catch (error: any) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  const fetchWeather = async () => {
    if (!filters.showWeather) return;
    
    setWeatherLoading(true);
    try {
      // Find the first event with a job that has an address
      let location = null;
      
      for (const event of events) {
        if (event.jobs?.property_address) {
          location = event.jobs.property_address;
          break;
        }
      }

      // If no job address found, try to use user's org location or default
      if (!location) {
        location = 'United States';
      }

      // Only fetch weather for visible date range
      let days = 7;
      if (view === 'month') days = 31;
      else if (view === 'week') days = 7;
      else if (view === 'day') days = 1;

      const { data, error } = await supabase.functions.invoke('get-weather-forecast', {
        body: {
          location: location,
          days: days
        }
      });

      if (error) throw error;
      setWeather(data.forecast || []);
      setWeatherLocation(data.location?.name + ', ' + data.location?.region || location);
    } catch (error: any) {
      console.error('Error fetching weather:', error);
    } finally {
      setWeatherLoading(false);
    }
  };

  const getEventsForDate = (date: Date) => {
    return events.filter(event => 
      isSameDay(new Date(event.start_time), date)
    );
  };

  const getWeatherForDate = (date: Date): WeatherData | undefined => {
    return weather.find(w => isSameDay(new Date(w.date), date));
  };

  const getWeatherIcon = (condition: string) => {
    if (condition.toLowerCase().includes('rain')) return <CloudRain className="h-4 w-4" />;
    if (condition.toLowerCase().includes('cloud')) return <Cloud className="h-4 w-4" />;
    if (condition.toLowerCase().includes('drizzle')) return <CloudDrizzle className="h-4 w-4" />;
    return <Sun className="h-4 w-4" />;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    onDateSelect(direction === 'prev' ? subMonths(selectedDate, 1) : addMonths(selectedDate, 1));
  };

  if (loading) {
    // Month View Skeleton
    if (view === 'month') {
      return (
        <div className="flex-1 flex flex-col bg-background">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="flex items-center gap-4">
              <Skeleton className="h-9 w-9" />
              <Skeleton className="h-7 w-40" />
              <Skeleton className="h-9 w-9" />
            </div>
            <Skeleton className="h-9 w-20" />
          </div>
          <div className="grid grid-cols-7 border-b border-border bg-muted/30">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="py-3 text-center text-sm font-semibold text-muted-foreground border-r border-border last:border-r-0">
                {day}
              </div>
            ))}
          </div>
          <div className="flex-1 grid grid-cols-7">
            {Array.from({ length: 35 }).map((_, i) => (
              <div key={i} className="border-r border-b border-border p-2 min-h-[120px]">
                <Skeleton className="h-5 w-8 mb-2" />
                <Skeleton className="h-6 w-full mb-1" />
                <Skeleton className="h-6 w-3/4" />
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    // Week View Skeleton
    if (view === 'week') {
      return (
        <div className="flex-1 flex flex-col bg-background">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <Skeleton className="h-7 w-60" />
            <Skeleton className="h-9 w-20" />
          </div>
          <div className="flex-1 grid grid-cols-7 gap-px bg-border">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="bg-background p-4">
                <Skeleton className="h-6 w-20 mb-4" />
                <Skeleton className="h-24 w-full mb-2" />
                <Skeleton className="h-24 w-full mb-2" />
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    // Day/Agenda View Skeleton
    return (
      <div className="flex-1 bg-background p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-9 w-20" />
          </div>
          <Skeleton className="h-32 w-full mb-4" />
          <Skeleton className="h-24 w-full mb-3" />
          <Skeleton className="h-24 w-full mb-3" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  // Month View - Full Screen Grid
  if (view === 'month') {
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    const startDate = startOfWeek(monthStart);
    const endDate = addDays(startDate, 34); // 5 weeks

    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return (
      <div className="flex-1 flex flex-col bg-background">
        {/* Month Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigateMonth('prev')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-bold">{format(selectedDate, 'MMMM yyyy')}</h2>
            <Button variant="outline" size="sm" onClick={() => navigateMonth('next')}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-4">
            {weatherLocation && (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Cloud className="h-4 w-4" />
                Weather for: {weatherLocation}
              </div>
            )}
            <Button variant="outline" size="sm" onClick={() => onDateSelect(new Date())}>
              Today
            </Button>
          </div>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b border-border bg-muted/30">
          {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
            <div key={day} className="py-3 text-center text-sm font-semibold text-muted-foreground border-r border-border last:border-r-0">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="flex-1 grid grid-cols-7 overflow-hidden">
          {days.map((day, i) => {
            const dayEvents = getEventsForDate(day);
            const dayWeather = getWeatherForDate(day);
            const isToday = isSameDay(day, new Date());
            const isCurrentMonth = day.getMonth() === selectedDate.getMonth();

            return (
              <div
                key={i}
                className={`border-r border-b border-border p-2 min-h-[120px] overflow-y-auto ${
                  !isCurrentMonth ? 'bg-muted/10' : ''
                } ${isToday ? 'bg-primary/5' : ''} hover:bg-accent/50 transition-colors cursor-pointer`}
                onClick={() => onDateSelect(day)}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className={`text-sm font-semibold ${isToday ? 'text-primary' : 'text-foreground'}`}>
                    {format(day, 'd')}
                  </span>
                  {filters.showWeather && (
                    weatherLoading ? (
                      <div className="h-4 w-12 bg-muted animate-pulse rounded" />
                    ) : dayWeather ? (
                      <div className="flex items-center gap-1 text-xs">
                        {getWeatherIcon(dayWeather.condition)}
                        <span className="font-medium">{Math.round(dayWeather.temp_f)}°</span>
                        {dayWeather.precip_chance > 30 && (
                          <span className="text-blue-500 font-semibold">{dayWeather.precip_chance}%</span>
                        )}
                      </div>
                    ) : null
                  )}
                </div>

                {/* Weather Warning for Contractors */}
                {dayWeather && dayWeather.precip_chance > 50 && (
                  <div className="mb-2 px-2 py-1 bg-blue-500/10 border border-blue-500/20 rounded text-[10px] text-blue-600 dark:text-blue-400">
                    ⚠️ High rain chance - Avoid outdoor work
                  </div>
                )}

                {/* Events */}
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map(event => (
                    <div
                      key={event.id}
                      className="text-xs p-1 rounded bg-primary/20 hover:bg-primary/30 truncate cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(event);
                      }}
                    >
                      {format(new Date(event.start_time), 'h:mm a')} - {event.title}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[10px] text-muted-foreground">
                      +{dayEvents.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Week View
  if (view === 'week') {
    const weekStart = startOfWeek(selectedDate);
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    return (
      <div className="flex-1 flex flex-col bg-background overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-xl font-bold">{format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}</h2>
          <Button variant="outline" size="sm" onClick={() => onDateSelect(new Date())}>
            Today
          </Button>
        </div>

        <div className="flex-1 grid grid-cols-7 gap-px bg-border overflow-auto">
          {weekDays.map(day => {
            const dayEvents = getEventsForDate(day);
            const dayWeather = getWeatherForDate(day);
            const isToday = isSameDay(day, new Date());

            return (
              <div key={day.toISOString()} className="bg-background p-4 min-h-[500px]">
                <div className="flex items-center justify-between mb-4">
                  <div className={`font-semibold ${isToday ? 'text-primary' : 'text-foreground'}`}>
                    {format(day, 'EEE d')}
                  </div>
                  {filters.showWeather && (
                    weatherLoading ? (
                      <div className="h-4 w-16 bg-muted animate-pulse rounded" />
                    ) : dayWeather ? (
                      <div className="flex items-center gap-2 text-sm">
                        {getWeatherIcon(dayWeather.condition)}
                        <span>{Math.round(dayWeather.temp_f)}°</span>
                      </div>
                    ) : null
                  )}
                </div>

                {dayWeather && dayWeather.precip_chance > 50 && (
                  <div className="mb-3 px-2 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded text-xs text-blue-600 dark:text-blue-400">
                    ⚠️ {dayWeather.precip_chance}% rain - Plan accordingly
                  </div>
                )}

                <div className="space-y-2">
                  {dayEvents.map(event => (
                    <EventCard 
                      key={event.id} 
                      event={event} 
                      onClick={() => onEventClick(event)} 
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Day View
  if (view === 'day') {
    const dayEvents = getEventsForDate(selectedDate);
    const dayWeather = getWeatherForDate(selectedDate);

    return (
      <div className="flex-1 bg-background p-6 overflow-auto">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</h2>
            <Button variant="outline" size="sm" onClick={() => onDateSelect(new Date())}>
              Today
            </Button>
          </div>

          {filters.showWeather && (
            weatherLoading ? (
              <div className="mb-6 p-4 bg-card border border-border rounded-lg">
                <div className="h-20 bg-muted animate-pulse rounded" />
              </div>
            ) : dayWeather ? (
              <div className="mb-6 p-4 bg-card border border-border rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {getWeatherIcon(dayWeather.condition)}
                    <div>
                      <div className="text-2xl font-bold">{Math.round(dayWeather.temp_f)}°F</div>
                      <div className="text-sm text-muted-foreground">{dayWeather.condition}</div>
                    </div>
                  </div>
                  {dayWeather.precip_chance > 30 && (
                    <div className="text-right">
                      <div className="text-lg font-semibold text-blue-500">{dayWeather.precip_chance}%</div>
                      <div className="text-xs text-muted-foreground">Chance of rain</div>
                    </div>
                  )}
                </div>
                {dayWeather.precip_chance > 50 && (
                  <div className="mt-3 px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded text-sm text-blue-600 dark:text-blue-400">
                    ⚠️ High rain probability - Consider rescheduling outdoor installations
                  </div>
                )}
              </div>
            ) : null
          )}

          <div className="space-y-3">
            {dayEvents.length === 0 ? (
              <p className="text-muted-foreground">No events scheduled</p>
            ) : (
              dayEvents.map(event => (
                <EventCard 
                  key={event.id} 
                  event={event} 
                  onClick={() => onEventClick(event)} 
                />
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // Agenda View
  return (
    <div className="flex-1 bg-background p-6 overflow-auto">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold mb-6">Upcoming Events</h2>
        {events.length === 0 ? (
          <p className="text-muted-foreground">No upcoming events</p>
        ) : (
          events.map(event => (
            <EventCard 
              key={event.id} 
              event={event} 
              onClick={() => onEventClick(event)} 
              showDate 
            />
          ))
        )}
      </div>
    </div>
  );
}
